import React from 'react';
import {
    Autocomplete,
    Paper,
    TextField,
    Box,
    Typography,
    Button,
    useTheme,
    CircularProgress,
    Collapse,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import {
    useUpdateOrganizationMutation,
    useEnableAdcWithDeliveryMutation
} from '../../../../features/organizations/organizationsApiSlice';
import { tokens } from '../../../../theme';

export const ADCDeliveryConfig = ({ adc_config = {}, organization = {} }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const navigate = useNavigate();

    const [updateOrganization] = useUpdateOrganizationMutation();
    const [enableAdcWithDelivery] = useEnableAdcWithDeliveryMutation();

    // ── Connected location (same as onboarding) ────────────────────────────────
    const onpremConfig = organization?.s3_config?.infra_data?.onprem_config;
    const isBucketCreated = onpremConfig?.bucket_created || false;
    const bucketName = onpremConfig?.onprem_bucket || '';
    const hasValidS3Location = Boolean(bucketName && isBucketCreated);



    // “Already configured” = has a bucket, and ADC delivery config points at it + has integration
    const expectedConnectedUri = hasValidS3Location
        ? `s3://${bucketName}/outgoing/cookie_sync/`
        : '';





    // ── Advanced/manual config state (existing) ───────────────────────────────
    const [deliveryType, setDeliveryType] = React.useState(adc_config.delivery_type || []);
    const [justEnabled, setJustEnabled] = React.useState(false);
    const [s3Uri, setS3Uri] = React.useState(adc_config.s3_uri || '');
    const [storageIntegration, setStorageIntegration] = React.useState(adc_config.storage_integration || '');
    const [snowflakeTable, setSnowflakeTable] = React.useState(adc_config.snowflake_table || '');
    const [recordedFormat, setRecordedFormat] = React.useState(adc_config.delivery_format || []);
    const configuredS3Uri = adc_config?.s3_uri || '';
    const configuredIntegration = adc_config?.storage_integration || '';
    const [advancedOpen, setAdvancedOpen] = React.useState(false);

    const isDeliveryConfigured =
        hasValidS3Location &&
        configuredS3Uri === expectedConnectedUri &&
        Boolean(configuredIntegration);

    // ── Step UI (copied from onboarding) ──────────────────────────────────────
    const [stepStatus, setStepStatus] = React.useState({
        location: null,
        integration: null,
        complete: null
    });
    const deliveryConfiguredEffective = isDeliveryConfigured || justEnabled;

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    const flattenFormats = (nestedArray) => nestedArray.reduce((acc, val) => acc.concat(val), []);

    const renderStep = (label, status) => {
        const color = {
            success: colors.greenAccent[400],
            pending: colors.grey[300],
            error: colors.redAccent[300]
        }[status];

        const icon = {
            success: '✅',
            pending: <CircularProgress size={16} />,
            error: '❌'
        }[status];

        return (
            <Box display="flex" alignItems="center" mb={1} gap={1}>
                <Box>{icon}</Box>
                <Typography variant="body1" color={color}>
                    {label}
                </Typography>
            </Box>
        );
    };

    // ── Primary CTA: enable at connected location + show steps ─────────────────
    const handleEnableDeliveryAtConnectedLocation = async () => {
        setError('');
        setLoading(true);
        setStepStatus({ location: 'pending', integration: 'pending', complete: 'pending' });

        try {
            // Step 1: Location validation
            if (!hasValidS3Location) {
                setStepStatus((prev) => ({ ...prev, location: 'error' }));
                setError('No valid S3 location is configured for this organization.');
                setLoading(false);
                return;
            } else {
                setStepStatus((prev) => ({ ...prev, location: 'success' }));
            }

            // Step 2: API call (integration + delivery config)
            await enableAdcWithDelivery({ id: organization.organization_id }).unwrap();

            // Step 3: Done
            setStepStatus((prev) => ({ ...prev, integration: 'success', complete: 'success' }));
            setJustEnabled(true);

            setTimeout(() => {
                setLoading(false);
            }, 500);
        } catch (err) {
            console.error(err);
            setStepStatus((prev) => ({ ...prev, integration: 'error', complete: 'error' }));
            setError(err?.data?.message || 'An unexpected error occurred.');
            setLoading(false);
        }
    };

    // ── Manual save (existing) ────────────────────────────────────────────────
    const handleDeliveryTypeChange = (event, newValue) => setDeliveryType(newValue);

    const handleInputChange = (event) => {
        if (event.target.name === 's3_uri') setS3Uri(event.target.value);
        else if (event.target.name === 'storage_integration') setStorageIntegration(event.target.value);
        else if (event.target.name === 'snowflake_table') setSnowflakeTable(event.target.value);
    };

    const handleFormatChange = (event, newValue) => setRecordedFormat(newValue);

    const onSaveOrganizationClicked = async () => {
        const payload = {
            id: organization._id || '',
            name: organization.name || '',
            adc_config: {
                delivery_type: deliveryType,
                s3_uri: s3Uri,
                storage_integration: storageIntegration,
                snowflake_table: snowflakeTable,
                delivery_format: recordedFormat
            }
        };

        await updateOrganization(payload);
        navigate('/console/organizations');
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Paper
                elevation={5}
                sx={{
                    background: `linear-gradient(135deg, ${colors.greenAccent[800]}, ${colors.primary[900]})`,
                    borderRadius: '20px',
                    padding: '20px',
                    width: '100%',
                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 1)',
                    overflow: 'hidden'
                }}
            >
                <Typography variant="h4" mb={1} color="textPrimary" sx={{ whiteSpace: 'pre-wrap' }}>
                    ADC Delivery Config
                </Typography>



                <Typography variant="body2" mt={1} color="textPrimary">
                    Connected output location:{' '}
                    {hasValidS3Location ? `s3://${bucketName}/outgoing/cookie_sync/` : 'No S3 location configured.'}
                </Typography>

                {deliveryConfiguredEffective && (
                    <Typography mt={2} sx={{ color: colors.greenAccent[400] }}>
                        ✅ ADC delivery is configured at the connected location.
                    </Typography>
                )}

                <Typography variant="body2" mt={1} color="textPrimary">
                    Current ADC delivery:{' '}
                    {adc_config?.s3_uri
                        ? adc_config.s3_uri
                        : justEnabled
                            ? expectedConnectedUri
                            : 'Not configured yet (use the button below, or Advanced).'}
                </Typography>
                {(adc_config?.storage_integration || justEnabled) && (
                    <Typography variant="body2" mt={1} color="textPrimary">
                        Storage integration:{' '}
                        {adc_config?.storage_integration
                            ? adc_config.storage_integration
                            : justEnabled
                                ? '(created/verified by automation)'
                                : ''}
                    </Typography>
                )}


                {!deliveryConfiguredEffective && (
                    <Box mt={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEnableDeliveryAtConnectedLocation}
                            disabled={loading || !hasValidS3Location}
                        >
                            Enable ADC Delivery at Connected Location
                        </Button>
                    </Box>
                )}

                {/* Step UI */}
                {loading && (
                    <Box mt={3}>
                        <Typography variant="h6" mb={1} color="textPrimary">
                            Enabling ADC + Delivery...
                        </Typography>
                        {renderStep('✔️ S3 Location configured', stepStatus.location)}
                        {renderStep('🔧 Storage Integration ready', stepStatus.integration)}
                        {renderStep('🚀 ADC with Delivery configured', stepStatus.complete)}
                    </Box>
                )}

                {error && (
                    <Typography mt={2} color="error">
                        {error}
                    </Typography>
                )}

                <Divider sx={{ my: 3, opacity: 0.35 }} />

                {/* Advanced (collapsed by default) */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setAdvancedOpen((v) => !v)}
                >
                    <Typography variant="h6" color="textPrimary">
                        Advanced (manual delivery settings)
                    </Typography>
                    {advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>

                <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
                    <Box mt={2}>
                        <Autocomplete
                            multiple
                            options={['S3', 'Snowflake']}
                            value={deliveryType}
                            name="delivery_type"
                            renderInput={(params) => <TextField {...params} variant="standard" label="Delivery Type" />}
                            onChange={handleDeliveryTypeChange}
                            sx={{ mb: 2 }}
                        />

                        {deliveryType.includes('S3') && (
                            <Box>
                                <TextField
                                    name="s3_uri"
                                    label="S3 URI"
                                    value={s3Uri}
                                    onChange={handleInputChange}
                                    sx={{ mb: 2 }}
                                    fullWidth
                                />
                                <TextField
                                    name="storage_integration"
                                    label="Storage Integration"
                                    value={storageIntegration}
                                    onChange={handleInputChange}
                                    sx={{ mb: 2 }}
                                    fullWidth
                                />
                                <Autocomplete
                                    multiple
                                    options={['CSV', 'Parquet']}
                                    value={flattenFormats(recordedFormat)}
                                    name="delivery_format"
                                    renderInput={(params) => <TextField {...params} variant="standard" label="Delivery Format" />}
                                    onChange={handleFormatChange}
                                    sx={{ mb: 2 }}
                                    fullWidth
                                />
                            </Box>
                        )}

                        {deliveryType.includes('Snowflake') && (
                            <TextField
                                name="snowflake_table"
                                label="Snowflake Table"
                                value={snowflakeTable}
                                onChange={handleInputChange}
                                sx={{ mb: 2 }}
                                fullWidth
                            />
                        )}

                        <Button onClick={onSaveOrganizationClicked} style={{ color: 'white', marginTop: '10px' }} sx={{ mb: 2 }}>
                            Save
                        </Button>
                    </Box>
                </Collapse>
            </Paper>
        </Box>
    );
};
